import { useEffect, useRef, useState } from 'react'
import { MEDIA_HOLD_MS, createMediaSessionHold, type MediaSessionHold } from '../services/media-session-hold'
import { vclog } from './vc-log'

/**
 * Whether the SFU session should exist: from the room channel's first join
 * until the channel has been down for MEDIA_HOLD_MS. A drop shorter than that
 * keeps the session it had — see services/media-session-hold.
 *
 * `sessionKey` names the session (room + peer). A new key — a breakout move, a
 * different meeting — starts over: nothing is held across it, so the old room's
 * session is torn down at once, as it always was.
 */
export function useMediaSessionHold(channelJoined: boolean, sessionKey: string | null): boolean {
  const [held, setHeld] = useState<{ key: string | null; active: boolean }>({ key: null, active: false })
  const holdRef = useRef<MediaSessionHold | null>(null)
  const wasJoinedRef = useRef(false)

  // Declared before the update effect: on a new key, the old hold is disposed
  // and the new one exists by the time that effect hands it the channel state.
  useEffect(() => {
    wasJoinedRef.current = false
    if (!sessionKey) return
    const hold = createMediaSessionHold({
      onChange: (active) => {
        if (!active) {
          vclog('join', 'room channel stayed down past the hold — ending the media session', {
            sessionKey, holdMs: MEDIA_HOLD_MS,
          })
        }
        setHeld({ key: sessionKey, active })
      },
    })
    holdRef.current = hold
    return () => {
      hold.dispose()
      if (holdRef.current === hold) holdRef.current = null
    }
  }, [sessionKey])

  useEffect(() => {
    const hold = holdRef.current
    if (!hold) return
    if (wasJoinedRef.current && !channelJoined) {
      vclog('join', 'room channel dropped — keeping the media session', { sessionKey, holdMs: MEDIA_HOLD_MS })
    }
    wasJoinedRef.current = channelJoined
    hold.update(channelJoined)
  }, [channelJoined, sessionKey])

  // `channelJoined` answers at once, so the first join is not a render late;
  // the held state only matters while the channel is down.
  return channelJoined || (held.key === sessionKey && held.active)
}
